/**
 * DS_06 — Review/video sheets for the Contoured Orthopedic Pillow PDP.
 * Theme-native port of the won Intelligems variant (the original `.ds_06`
 * body-class scoping is replaced by a handle-gated asset + BEM block).
 *
 * Runs at ALL widths since the Aug 2026 desktop-parity rollout:
 *   - Mobile (<= 767px): full-height bottom sheets (the original behavior,
 *     unchanged).
 *   - Desktop (>= 768px): the same chips open CENTERED POPUPS (styles in the
 *     scss), with Escape-to-close. The desktop review popup shows the widget
 *     as Judge.me rendered it — the mobile-only trim/refetch machinery is
 *     deliberately skipped (validated in the Aug 2026 Intelligems DCOP
 *     desktop test).
 *
 * The two trigger chips are SERVER-RENDERED at the top of the header
 * product-block (snippets/ds06-pdp-triggers.liquid), complete with Judge.me
 * numbers from the badge metafield — this file never positions or fills them;
 * it only wires taps and hides a chip whose dependency turns out broken. The
 * sheets are populated by RELOCATING the live third-party app DOM into them:
 *   - Video sheet  : moves the single `.reeview-app-widget` (Videowise) in on open,
 *                    back to its captured origin on close.
 *   - Review sheet : moves the HYDRATED `#judgeme_product_reviews` node in on open,
 *                    back to its captured origin on close. The live DOM has TWO
 *                    nodes with that id, so we target the one that actually
 *                    contains rendered `.jdgm-rev` items — never a bare id lookup.
 *
 * Deviations from the won code (all intentional, see build brief):
 *   - Review node is moved on OPEN and restored on CLOSE (the won code moved it at
 *     setup and never restored, leaving the on-page review section empty).
 *   - Sheet wrappers use z-index 9650 (the won 1000 sat below this theme's stack).
 *   - `window.optiReady` / `optiActiveTests` and the analytics block are dropped.
 *
 * PORTABILITY: the built bundle (assets/endrock.*-ds06-pdp-bottom-sheets.*) is
 * copied verbatim to dosazeUK / dosazeAU, which have no build toolchain (see
 * their PORTED-FROM.md ledgers). This file is the only source of truth — keep
 * it working on all three themes; never fork it downstream.
 */

const MOBILE_MAX_WIDTH = 767;
const SCROLL_LOCK_CLASS = 'js-ds06-locked';
const TRIGGER_HIDDEN_CLASS = 'ds06-sheet__trigger--hidden';
const HIDE_CARD_STYLE = '.vw-cmp__shoppable--cardv2 { display: none !important; }';
const TRIMMED_PER_PAGE = '5';
const LOAD_RETRY_INTERVAL = 4000;
const LOAD_RETRY_MAX = 2;
const POLL_INTERVAL = 500;
const POLL_TIMEOUT = 10000;
// Passed to Videowise's documented `window.videowiseRenderWidgetThreshold`
// override (see ensureVideowiseReady): any value larger than the page height
// makes its lazy-render position check pass at every scroll position.
const VIDEOWISE_FORCE_RENDER_THRESHOLD = 100000;
// Was a 2s hide-on-failure deadline. Intelligems' QA (Aug 2026 desktop test)
// caught Videowise finishing to load JUST past 2s with the chip already
// permanently hidden — widget fine, chip lost, lopsided one-chip row. The
// same cliff existed here on mobile (worse on slow connections). Now: a
// generous window before hiding, plus a recovery window that UN-hides the
// chip if the widget turns up late.
const VIDEOWISE_READY_TIMEOUT = 15000;
const VIDEOWISE_RECOVERY_TIMEOUT = 30000;

class Ds06BottomSheets {
  constructor() {
    this.ownsLock = false;
    this.reviewTrimmed = false;
    this.reviewLoadMoreClicked = false;
    this.videoOrigin = null;
    this.reviewOrigin = null;
    this.summaryOrigin = null;
    this.trimStash = null;
    this.init();
  }

  init() {
    // All widths since the desktop-parity rollout. The asset is already
    // handle-gated in Liquid, so no URL check. Mode is decided once at load,
    // matching how the width gate always behaved (no resize handling).
    // Two buy boxes host these sheets: the classic gallery-testimonials meta
    // (.variant-size-selector) and the Unified Buy Box (.ubx), whose title is
    // a direct child of the meta rather than inside a .product-block.
    this.isDesktop = window.innerWidth > MOBILE_MAX_WIDTH;
    this.waitFor(
      () =>
        document.querySelector(
          '.product-single__meta.variant-size-selector .product-block h1'
        ) || document.querySelector('.ubx .product-single__meta h1.product-single__title')
    ).then((h1) => {
      if (h1) {
        this.setup();
      }
    });
  }

  /**
   * Poll `condition` every 500ms until it returns a truthy value, then resolve
   * with that value. Resolves with null if it never becomes truthy within
   * `timeout` (the trigger then simply stays hidden — dependency-guarded init).
   *
   * Recursive setTimeout, NOT setInterval — this bundle is copied to sites
   * whose themes interfere with intervals (DosazeAU's inline "iOS scroll fix"
   * scripts filter setInterval callbacks and mass-clear intervals created in
   * the first second after DOMContentLoaded, which killed this poll on iOS).
   * setTimeout chains are untouched by those scripts.
   */
  waitFor(condition, timeout = POLL_TIMEOUT) {
    return new Promise((resolve) => {
      const initial = condition();
      if (initial) {
        resolve(initial);
        return;
      }
      const deadline = Date.now() + timeout;
      const poll = () => {
        const value = condition();
        if (value) {
          resolve(value);
          return;
        }
        if (Date.now() >= deadline) {
          resolve(null);
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      };
      setTimeout(poll, POLL_INTERVAL);
    });
  }

  setup() {
    this.triggers = document.querySelector('.ds06-sheet__triggers');
    this.videoWrapper = document.querySelector('.ds06-sheet__video-wrapper');
    this.reviewWrapper = document.querySelector('.ds06-sheet__review-wrapper');
    if (!this.triggers || !this.videoWrapper || !this.reviewWrapper) {
      return;
    }

    // The triggers are server-rendered in place; only the fixed-position
    // sheets move — to <body> for a clean stacking context + scroll-lock
    // parity. They ship with inline display:none (unstyled flash before CSS
    // loads otherwise); once we're initializing at either width, hand control
    // back to the stylesheet (hidden until --is-open).
    document.body.appendChild(this.videoWrapper);
    document.body.appendChild(this.reviewWrapper);
    this.videoWrapper.style.removeProperty('display');
    this.reviewWrapper.style.removeProperty('display');

    this.setupVideo();
    this.setupReviews();
    this.wireTriggers();
    this.observeShadowRoot();
  }

  /* ── Video sheet ─────────────────────────────────────────────────────── */

  setupVideo() {
    const trigger = this.triggers.querySelector('.ds06-sheet__video-trigger');
    if (!trigger) {
      return;
    }
    // Scope to the buy-box meta rather than `.product-block`: in the US the
    // Videowise widget is a raw `custom` block inside `.product-block`, but on
    // UK/AU it's the Videowise app block, which Shopify wraps in a sibling
    // `.shopify-app-block` outside `.product-block`. This selector matches both.
    // Page-wide fallback: on the DESKTOP layout of the DCOP template the widget
    // sits in a desktop-only product block OUTSIDE that meta entirely (found
    // during the Aug 2026 desktop test) — prefer the meta match, fall back to
    // anywhere on the page.
    this.waitFor(() =>
      document.querySelector('.product-single__meta.variant-size-selector .reeview-app-widget') ||
      document.querySelector('.reeview-app-widget')
    ).then(
      (widget) => {
        if (!widget) {
          trigger.classList.add(TRIGGER_HIDDEN_CLASS);
          return;
        }
        this.videoWidget = widget;
        this.videoOrigin = { parent: widget.parentNode, nextSibling: widget.nextSibling };
        this.ensureVideowiseReady(trigger);
      }
    );
  }

  /**
   * Make sure Videowise actually renders, and hide the (server-visible) video
   * chip if it never does. Videowise lazy-renders
   * its widget only while the mount's parent sits within a threshold of the
   * viewport (default: 2 viewport heights), re-checking on every scroll
   * event. The won variant forced that check to pass by pinning the viewport
   * 700px down for up to 2s — which read as "the top of the page loads late"
   * on every mobile visit. Instead, raise Videowise's own threshold override
   * so the check passes at any scroll position, and fire a synthetic scroll
   * event so its already-attached listener re-evaluates. The viewport never
   * moves. Order-safe: if Videowise's client.js loads after us, its immediate
   * on-load check reads the raised threshold directly.
   *
   * The chip is rendered VISIBLE by the Liquid (first paint shows the final
   * layout); this only hides it in the genuine-failure case, instead of the old
   * hidden-then-reveal contract that popped the buy box down on every load.
   * Hide only after a generous window, and UN-hide if the widget turns up
   * within a further recovery window — the chip appears late rather than being
   * lost to a slow load (see the VIDEOWISE_READY_TIMEOUT comment).
   */
  ensureVideowiseReady(trigger) {
    if (window.videowiseShadowRoots) {
      return;
    }
    window.videowiseRenderWidgetThreshold = VIDEOWISE_FORCE_RENDER_THRESHOLD;
    window.dispatchEvent(new Event('scroll'));
    this.waitFor(() => window.videowiseShadowRoots, VIDEOWISE_READY_TIMEOUT).then((ready) => {
      if (ready) {
        return;
      }
      trigger.classList.add(TRIGGER_HIDDEN_CLASS);
      this.waitFor(() => window.videowiseShadowRoots, VIDEOWISE_RECOVERY_TIMEOUT).then(
        (recovered) => {
          if (recovered) {
            trigger.classList.remove(TRIGGER_HIDDEN_CLASS);
          }
        }
      );
    });
  }

  openVideo() {
    if (!this.videoWidget) {
      return;
    }
    // The chip can be visible while Videowise is still loading (the patience
    // window in ensureVideowiseReady) — never open an empty sheet for a tap
    // in that window.
    if (!window.videowiseShadowRoots) {
      return;
    }
    const vwWrapper = this.videoWrapper.querySelector('.ds06-sheet__video-vw-wrapper');
    vwWrapper.insertAdjacentElement('afterbegin', this.videoWidget);
    this.lockScroll();
    this.videoWrapper.classList.add('ds06-sheet__video-wrapper--is-open');
  }

  closeVideo() {
    this.videoWrapper.classList.remove('ds06-sheet__video-wrapper--is-open');
    this.unlockScroll();
    // Sweep everything back — the widget reference plus anything the app
    // re-rendered while it sat in the sheet.
    const vwWrapper = this.videoWrapper.querySelector('.ds06-sheet__video-vw-wrapper');
    Array.prototype.slice.call(vwWrapper.children).forEach((el) => {
      this.restore(el, this.videoOrigin);
    });
  }

  /* ── Review sheet ────────────────────────────────────────────────────── */

  /**
   * Find a rendered `.jdgm-rev` inside whichever `#judgeme_product_reviews` node
   * is actually hydrated. The live DOM has two id-colliding nodes; a bare id
   * lookup would grab the wrong one.
   */
  findHydratedReviewRev() {
    const nodes = document.querySelectorAll('#judgeme_product_reviews');
    for (let i = 0; i < nodes.length; i += 1) {
      const rev = nodes[i].querySelector('.jdgm-rev-widg .jdgm-rev');
      if (rev) {
        return rev;
      }
    }
    return null;
  }

  /**
   * The review chip is server-rendered visible, numbers included (Judge.me
   * badge metafield). This only captures the hydrated widget node the sheet
   * relocates on open — and hides the chip if hydration never happens, so a
   * tap can never land on a dead chip.
   */
  setupReviews() {
    const trigger = this.triggers.querySelector('.ds06-sheet__review-trigger');
    this.waitFor(() => this.findHydratedReviewRev()).then((rev) => {
      const widg = rev && rev.closest('.jdgm-rev-widg');
      const node = rev && rev.closest('#judgeme_product_reviews');
      if (!widg || !node) {
        if (trigger) {
          trigger.classList.add(TRIGGER_HIDDEN_CLASS);
        }
        return;
      }
      this.reviewNode = node;
    });
  }

  /**
   * Find the whole hydrated `#judgeme_product_reviews` node (not just a rev),
   * skipping any copy currently relocated inside our own wrapper. Judge.me can
   * re-render its widget as a FRESH node at any time, so the desktop path
   * re-finds on every open rather than trusting a reference captured at init
   * (lesson from the Aug 2026 desktop test).
   */
  findHydratedReviewNode() {
    const nodes = document.querySelectorAll('#judgeme_product_reviews');
    for (let i = 0; i < nodes.length; i += 1) {
      if (
        !this.reviewWrapper.contains(nodes[i]) &&
        nodes[i].querySelector('.jdgm-rev-widg .jdgm-rev')
      ) {
        return nodes[i];
      }
    }
    return null;
  }

  /**
   * Desktop popup: show the widget exactly as Judge.me rendered it. The
   * mobile-only trim/refetch machinery is intentionally skipped — Judge.me's
   * own load-more keeps working inside the popup.
   */
  openReviewDesktop() {
    const node = this.findHydratedReviewNode() || this.reviewNode;
    if (!node) {
      return;
    }
    this.reviewNode = node;
    this.reviewOrigin = { parent: node.parentNode, nextSibling: node.nextSibling };
    const summaryEl = node.querySelector('.jdgm-rev-widg__summary');
    this.summaryOrigin = summaryEl
      ? { parent: summaryEl.parentNode, nextSibling: summaryEl.nextSibling }
      : null;

    const listWrapper = this.reviewWrapper.querySelector('.ds06-sheet__review-list');
    const summaryWrapper = this.reviewWrapper.querySelector('.ds06-sheet__review-summary');
    node.querySelectorAll('.jdgm-rev-widg__summary').forEach((s) => {
      summaryWrapper.insertAdjacentElement('afterbegin', s);
    });
    listWrapper.insertAdjacentElement('afterbegin', node);

    this.lockScroll();
    this.reviewWrapper.classList.add('ds06-sheet__review-wrapper--is-open');
  }

  closeReviewDesktop() {
    this.reviewWrapper.classList.remove('ds06-sheet__review-wrapper--is-open');
    this.unlockScroll();
    // Summaries back inside the node first, then EVERYTHING left in the popup
    // list back to the on-page origin — sweeping the popup empty even if
    // Judge.me re-rendered a fresh node while it sat in the popup, so the
    // on-page review section is never left empty.
    this.reviewWrapper
      .querySelectorAll('.ds06-sheet__review-summary > .jdgm-rev-widg__summary')
      .forEach((s) => {
        if (this.summaryOrigin) {
          this.restore(s, this.summaryOrigin);
        } else if (this.reviewNode) {
          this.reviewNode.insertAdjacentElement('afterbegin', s);
        }
      });
    const listWrapper = this.reviewWrapper.querySelector('.ds06-sheet__review-list');
    Array.prototype.slice.call(listWrapper.children).forEach((el) => {
      this.restore(el, this.reviewOrigin);
    });
  }

  openReview() {
    if (this.isDesktop) {
      this.openReviewDesktop();
      return;
    }
    if (!this.reviewNode) {
      return;
    }
    const listWrapper = this.reviewWrapper.querySelector('.ds06-sheet__review-list');
    const summaryWrapper = this.reviewWrapper.querySelector('.ds06-sheet__review-summary');

    // One-time: capture origins + run the destructive trim while the node is
    // still in its freshly-hydrated state.
    if (!this.reviewTrimmed) {
      this.reviewTrimmed = true;
      this.reviewOrigin = {
        parent: this.reviewNode.parentNode,
        nextSibling: this.reviewNode.nextSibling,
      };
      const summaryEl = this.reviewNode.querySelector('.jdgm-rev-widg__summary');
      if (summaryEl) {
        this.summaryOrigin = { parent: summaryEl.parentNode, nextSibling: summaryEl.nextSibling };
      }
      this.trimReviews();
    }

    // Per-open: relocate the node (and its summary) into the sheet.
    this.reviewNode.querySelectorAll('.jdgm-rev-widg__summary').forEach((summaryEl) => {
      summaryWrapper.insertAdjacentElement('afterbegin', summaryEl);
    });
    listWrapper.insertAdjacentElement('afterbegin', this.reviewNode);

    // First open: only auto-paginate when the trim actually cleared the list
    // (this.trimStash set). If the trim was skipped — no paginate, or no
    // reviews — the server-rendered reviews already on the node stay as-is;
    // clicking load-more here would append an overlapping page and duplicate
    // reviews (Judge.me's persisted pagination state can drift off 8).
    if (!this.reviewLoadMoreClicked && this.trimStash) {
      this.reviewLoadMoreClicked = true;
      this.requestReviewPage(0);
    }

    this.lockScroll();
    this.reviewWrapper.classList.add('ds06-sheet__review-wrapper--is-open');
  }

  trimReviews() {
    const revs = this.reviewNode.querySelectorAll('.jdgm-rev');
    const loadMore = this.reviewNode.querySelector(
      '.jdgm-paginate[data-per-page] .jdgm-paginate__load-more'
    );
    if (!revs.length || !loadMore) {
      return;
    }
    const paginate = loadMore.closest('.jdgm-paginate');
    // Keep the removed reviews + original paginate state so requestReviewPage
    // can restore them if the refetch never lands.
    this.trimStash = {
      revs: Array.from(revs),
      parent: revs[0].parentNode,
      perPage: paginate.getAttribute('data-per-page'),
      page: loadMore.getAttribute('data-page'),
    };
    revs.forEach((rev) => rev.remove());
    loadMore.setAttribute('data-page', '1');
    paginate.setAttribute('data-per-page', TRIMMED_PER_PAGE);
  }

  /**
   * Click Judge.me's load-more and verify reviews actually render. Judge.me's
   * fetch has no error path — it inline-shows the spinner and inline-hides the
   * paginate before the request, and only reverses that on success — so one
   * transient network failure would otherwise leave the sheet empty with a
   * stuck spinner forever. Re-click while the widget has zero reviews (the
   * zero-check prevents double-loading a page that did land); after the last
   * attempt, restore the trimmed server-rendered reviews.
   */
  requestReviewPage(attempt) {
    const loadMore = this.reviewNode.querySelector('.jdgm-paginate__load-more');
    if (loadMore) {
      loadMore.click();
    }
    setTimeout(() => {
      if (this.reviewNode.querySelectorAll('.jdgm-rev').length > 0) {
        this.dedupeReviews();
        return;
      }
      if (attempt < LOAD_RETRY_MAX) {
        this.requestReviewPage(attempt + 1);
      } else {
        this.restoreTrimmedReviews();
      }
    }, LOAD_RETRY_INTERVAL);
  }

  /**
   * Last-resort fallback: every fetch attempt failed, so put the original
   * server-rendered reviews and paginate state back, and clear the inline
   * show/hide that Judge.me applied before its failed fetch (spinner shown,
   * paginate hidden). Worst case the shopper sees the widget exactly as
   * Judge.me first rendered it — never an empty sheet.
   */
  restoreTrimmedReviews() {
    if (!this.trimStash || this.reviewNode.querySelectorAll('.jdgm-rev').length > 0) {
      return;
    }
    // A rendered response may have replaced the original list container; fall
    // back to whatever reviews container the widget has now.
    const parent = this.trimStash.parent.isConnected
      ? this.trimStash.parent
      : this.reviewNode.querySelector('.jdgm-rev-widg__reviews');
    if (!parent) {
      return;
    }
    this.trimStash.revs.forEach((rev) => parent.appendChild(rev));
    const paginate = this.reviewNode.querySelector('.jdgm-paginate');
    const loadMore = this.reviewNode.querySelector('.jdgm-paginate__load-more');
    const spinner = this.reviewNode.querySelector('.jdgm-rev-widg__paginate-spinner-wrapper');
    if (paginate) {
      paginate.setAttribute('data-per-page', this.trimStash.perPage);
      paginate.style.removeProperty('display');
    }
    if (loadMore) {
      loadMore.setAttribute('data-page', this.trimStash.page);
    }
    if (spinner) {
      spinner.style.removeProperty('display');
    }
  }

  /**
   * Remove any `.jdgm-rev` whose review id already appears earlier in the node.
   * Judge.me can re-render an overlapping page when its persisted pagination
   * state drifts, so this is a cheap, idempotent backstop on top of the trim —
   * the sheet never shows the same review twice. No-op when there are no
   * duplicates (the normal first-paint case).
   */
  dedupeReviews() {
    if (!this.reviewNode) {
      return;
    }
    const seen = new Set();
    this.reviewNode.querySelectorAll('.jdgm-rev').forEach((rev) => {
      const id = rev.getAttribute('data-review-id');
      if (!id) {
        return;
      }
      if (seen.has(id)) {
        rev.remove();
      } else {
        seen.add(id);
      }
    });
  }

  closeReview() {
    if (this.isDesktop) {
      this.closeReviewDesktop();
      return;
    }
    this.reviewWrapper.classList.remove('ds06-sheet__review-wrapper--is-open');
    this.unlockScroll();
    // Put the summary back inside the node first, then the node back on page —
    // so the on-page review section is never left empty.
    const summaryEl = this.reviewWrapper.querySelector('.jdgm-rev-widg__summary');
    if (summaryEl && this.summaryOrigin) {
      this.restore(summaryEl, this.summaryOrigin);
    }
    this.restore(this.reviewNode, this.reviewOrigin);
  }

  /* ── Shared wiring ───────────────────────────────────────────────────── */

  wireTriggers() {
    this.triggers.addEventListener('click', (event) => {
      if (event.target.closest('.ds06-sheet__video-trigger')) {
        this.openVideo();
      } else if (event.target.closest('.ds06-sheet__review-trigger')) {
        this.openReview();
      }
    });
    this.videoWrapper.addEventListener('click', (event) => {
      if (event.target.closest('.ds06-sheet__video-overlay, .ds06-sheet__video-close')) {
        this.closeVideo();
      }
    });
    this.reviewWrapper.addEventListener('click', (event) => {
      if (event.target.closest('.ds06-sheet__review-overlay, .ds06-sheet__review-close')) {
        this.closeReview();
      }
    });
    // Desktop-native affordance; inert on touch devices (no Escape key).
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (this.videoWrapper.classList.contains('ds06-sheet__video-wrapper--is-open')) {
        this.closeVideo();
      } else if (this.reviewWrapper.classList.contains('ds06-sheet__review-wrapper--is-open')) {
        this.closeReview();
      }
    });
  }

  /**
   * Hide the Videowise shoppable card inside the React modal portal's shadow root
   * while the video sheet is open. Guarded so it injects only once the portal and
   * its shadow root actually exist (no silent no-op on a cold open).
   */
  observeShadowRoot() {
    this.injectShadowHideStyle();
    const observer = new MutationObserver(() => this.injectShadowHideStyle());
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  injectShadowHideStyle() {
    if (!this.videoWrapper.classList.contains('ds06-sheet__video-wrapper--is-open')) {
      return;
    }
    const content = document.querySelector('.ReactModalPortal .ReactModal__Content');
    if (!content || !content.shadowRoot) {
      return;
    }
    if (content.shadowRoot.querySelector('style.ds06-sheet__hide-card')) {
      return;
    }
    const style = document.createElement('style');
    style.className = 'ds06-sheet__hide-card';
    style.textContent = HIDE_CARD_STYLE;
    content.shadowRoot.prepend(style);
  }

  /* ── Body scroll lock ────────────────────────────────────────────────── */

  lockScroll() {
    // Guards only against OUR OWN lock class (e.g. both sheets toggling in one
    // session). Other theme modals (sleep-quiz, complete-products) use their own
    // lock mechanisms this does not detect — a sheet opening over one of those
    // would still apply body position:fixed. Untested; triggers sit behind the
    // full-screen sheets, so the overlap is not reachable in normal flows.
    if (document.body.classList.contains(SCROLL_LOCK_CLASS)) {
      return;
    }
    document.body.style.setProperty('--ds06-scroll-y', `-${window.scrollY}px`);
    document.body.classList.add(SCROLL_LOCK_CLASS);
    this.ownsLock = true;
  }

  unlockScroll() {
    // No-op the unlock if this instance didn't take the lock above.
    if (!this.ownsLock) {
      return;
    }
    const offset = parseInt(document.body.style.getPropertyValue('--ds06-scroll-y'), 10);
    const restoreTop = Number.isNaN(offset) ? 0 : -offset;
    document.body.classList.remove(SCROLL_LOCK_CLASS);
    document.body.style.removeProperty('--ds06-scroll-y');
    window.scrollTo({ top: restoreTop, behavior: 'instant' });
    this.ownsLock = false;
  }

  restore(node, origin) {
    if (!node || !origin || !origin.parent) {
      return;
    }
    if (origin.nextSibling && origin.nextSibling.parentNode === origin.parent) {
      origin.parent.insertBefore(node, origin.nextSibling);
    } else {
      origin.parent.appendChild(node);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Ds06BottomSheets(); // eslint-disable-line no-new
});
